import {
  Module,
  ModuleCategory,
  ModulePlan,
  ModulePermission,
  ModuleUsageStats,
  CreateModuleDto,
  UpdateModuleDto,
  CreateModuleCategoryDto,
  UpdateModuleCategoryDto,
  CreateModulePlanDto,
  UpdateModulePlanDto,
  FindModulesDto,
  ModuleUsageQueryDto,
  PaginatedResponse,
} from '../types/module.types';
import { adminApi } from '@/config/axios.config';

// API de Módulos
export const modulesApiService = {
  // Obtener todos los módulos
  getModules: async (
    query: FindModulesDto = {}
  ): Promise<PaginatedResponse<Module>> => {
    const response = await adminApi.get('/modules', { params: query });
    return response.data;
  },

  // Obtener un módulo por ID
  getModule: async (id: string): Promise<Module> => {
    const response = await adminApi.get(`/modules/${id}`);
    return response.data;
  },

  // Crear un nuevo módulo
  createModule: async (data: CreateModuleDto): Promise<Module> => {
    const response = await adminApi.post('/modules', data);
    return response.data;
  },

  // Actualizar un módulo
  updateModule: async (id: string, data: UpdateModuleDto): Promise<Module> => {
    const response = await adminApi.put(`/modules/${id}`, data);
    return response.data;
  },

  // Eliminar un módulo
  deleteModule: async (id: string): Promise<void> => {
    await adminApi.delete(`/modules/${id}`);
  },

  // Duplicar un módulo
  duplicateModule: async (id: string): Promise<Module> => {
    const response = await adminApi.post(`/modules/${id}/duplicate`);
    return response.data;
  },

  // Cambiar estado de un módulo
  toggleModuleStatus: async (id: string): Promise<Module> => {
    const response = await adminApi.put(`/modules/${id}/status`);
    return response.data;
  },

  // Obtener estadísticas de uso de un módulo
  getModuleUsage: async (
    id: string,
    query: ModuleUsageQueryDto = {}
  ): Promise<ModuleUsageStats> => {
    const response = await adminApi.get(`/modules/${id}/usage`, {
      params: query,
    });
    return response.data;
  },

  // Obtener permisos de un módulo
  getModulePermissions: async (id: string): Promise<ModulePermission[]> => {
    const response = await adminApi.get(`/modules/${id}/permissions`);
    const data = response.data?.data || response.data;
    return Array.isArray(data) ? data : [];
  },

  // Actualizar permisos de un módulo
  updateModulePermissions: async (
    id: string,
    permissions: ModulePermission[]
  ): Promise<ModulePermission[]> => {
    const response = await adminApi.put(`/modules/${id}/permissions`, {
      permissions,
    });
    const data = response.data?.data || response.data;
    return Array.isArray(data) ? data : [];
  },
};

// API de Categorías de Módulos
export const moduleCategoriesApiService = {
  // Obtener todas las categorías
  getCategories: async (): Promise<ModuleCategory[]> => {
    const response = await adminApi.get('/module-categories');
    return response.data;
  },

  // Obtener estadísticas de categorías
  getCategoryStats: async (): Promise<
    Array<{ category: ModuleCategory; moduleCount: number }>
  > => {
    const response = await adminApi.get('/module-categories/stats');
    return response.data;
  },

  // Obtener una categoría por ID
  getCategory: async (id: string): Promise<ModuleCategory> => {
    const response = await adminApi.get(`/module-categories/${id}`);
    return response.data;
  },

  // Obtener módulos de una categoría
  getModulesByCategory: async (id: string): Promise<Module[]> => {
    const response = await adminApi.get(
      `/module-categories/${id}/modules`
    );
    return response.data;
  },

  // Crear una nueva categoría
  createCategory: async (
    data: CreateModuleCategoryDto
  ): Promise<ModuleCategory> => {
    const response = await adminApi.post('/module-categories', data);
    return response.data;
  },

  // Actualizar una categoría
  updateCategory: async (
    id: string,
    data: UpdateModuleCategoryDto
  ): Promise<ModuleCategory> => {
    const response = await adminApi.put(
      `/module-categories/${id}`,
      data
    );
    return response.data;
  },

  // Eliminar una categoría
  deleteCategory: async (id: string): Promise<void> => {
    await adminApi.delete(`/module-categories/${id}`);
  },
};

// API de Planes de Módulos
export const modulePlansApiService = {
  // Obtener todos los planes de módulos
  getModulePlans: async (): Promise<ModulePlan[]> => {
    const response = await adminApi.get('/module-plans');
    return response.data;
  },

  // Obtener un plan de módulo por ID
  getModulePlan: async (id: string): Promise<ModulePlan> => {
    const response = await adminApi.get(`/module-plans/${id}`);
    return response.data;
  },

  // Obtener planes de módulos por módulo
  getModulePlansByModule: async (moduleId: string): Promise<ModulePlan[]> => {
    const response = await adminApi.get(
      `/module-plans/module/${moduleId}`
    );
    return response.data;
  },

  // Obtener planes de módulos por plan
  getModulePlansByPlan: async (planId: string): Promise<ModulePlan[]> => {
    const response = await adminApi.get(`/module-plans/plan/${planId}`);
    return response.data;
  },

  // Obtener módulos requeridos de un plan
  getRequiredModules: async (planId: string): Promise<ModulePlan[]> => {
    const response = await adminApi.get(
      `/module-plans/plan/${planId}/required`
    );
    return response.data;
  },

  // Obtener módulos add-on de un plan
  getAddonModules: async (planId: string): Promise<ModulePlan[]> => {
    const response = await adminApi.get(
      `/module-plans/plan/${planId}/addons`
    );
    return response.data;
  },

  // Obtener precio de un módulo en un plan
  getModulePrice: async (
    moduleId: string,
    planId: string
  ): Promise<{ moduleId: string; planId: string; price: number | null }> => {
    const response = await adminApi.get(
      `/module-plans/price/${moduleId}/${planId}`
    );
    return response.data;
  },

  // Crear un nuevo plan de módulo
  createModulePlan: async (data: CreateModulePlanDto): Promise<ModulePlan> => {
    const response = await adminApi.post('/module-plans', data);
    return response.data;
  },

  // Actualizar un plan de módulo
  updateModulePlan: async (
    id: string,
    data: UpdateModulePlanDto
  ): Promise<ModulePlan> => {
    const response = await adminApi.put(`/module-plans/${id}`, data);
    return response.data;
  },

  // Eliminar un plan de módulo
  deleteModulePlan: async (id: string): Promise<void> => {
    await adminApi.delete(`/module-plans/${id}`);
  },

  // Eliminar un plan de módulo por módulo y plan
  deleteModulePlanByModuleAndPlan: async (
    moduleId: string,
    planId: string
  ): Promise<void> => {
    await adminApi.delete(
      `/module-plans/module/${moduleId}/plan/${planId}`
    );
  },
};

// API de Permisos RBAC
export const permissionsApiService = {
  // Obtener permisos agrupados por recurso
  getPermissionsGroupedByResource: async (): Promise<any> => {
    const response = await adminApi.get('/permissions/grouped-by-resource');
    return response.data;
  },
};

// Exportar todas las APIs
export const modulesApi = {
  modules: modulesApiService,
  categories: moduleCategoriesApiService,
  plans: modulePlansApiService,
  permissions: permissionsApiService,
};
